
import { Component, ElementRef, HostListener, ViewChild, computed, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalSearchResult, SearchResultBot, SearchResultDocument, SearchResultProject } from '@shared/index';
import { DialogModule } from 'primeng/dialog';
import { GlobalSearchService } from '../../services/global-search.service';

type SearchResultItem =
  | { type: 'project'; data: SearchResultProject }
  | { type: 'bot'; data: SearchResultBot }
  | { type: 'document'; data: SearchResultDocument };

@Component({
  selector: 'app-global-search',
  imports: [DialogModule],
  templateUrl: './global-search.component.html',
  styleUrl: './global-search.component.scss'
})
export class GlobalSearchComponent {
  @ViewChild('searchInput') public searchInput!: ElementRef<HTMLInputElement>;
  public readonly closed = output();

  public visible = signal(false);
  public query = signal('');
  public results = signal<GlobalSearchResult | null>(null);
  public loading = signal(false);
  public selectedIndex = signal(-1);
  public flatResults = computed<SearchResultItem[]>(() => {
    const r = this.results();
    if (!r) return [];
    return [
      ...r.projects.map(data => ({ type: 'project' as const, data })),
      ...r.bots.map(data => ({ type: 'bot' as const, data })),
      ...r.documents.map(data => ({ type: 'document' as const, data }))
    ];
  });
  public hasResults = computed(() => this.flatResults().length > 0);
  public hasQuery = computed(() => this.query().trim().length > 0);

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private searchService: GlobalSearchService,
    private router: Router
  ) { }

  @HostListener('document:keydown', ['$event'])
  public onKeydown(event: KeyboardEvent): void {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.toggle();
    }
  }

  public toggle(): void {
    if (this.visible()) {
      this.visible.set(false);
    } else {
      this.open();
    }
  }

  public open(): void {
    this.visible.set(true);
    this.query.set('');
    this.results.set(null);
    this.selectedIndex.set(-1);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 100);
  }

  public close(): void {
    this.visible.set(false);
    // TODO: The 'emit' function requires a mandatory void argument
    this.closed.emit();
  }

  public onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.query.set(value);

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (value.trim().length === 0) {
      this.results.set(null);
      this.selectedIndex.set(-1);
      return;
    }

    this.debounceTimer = setTimeout(() => {
      void this.performSearch(value);
    }, 300);
  }

  public onKeydownInInput(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.selectedIndex() < this.flatResults().length - 1) {
        this.selectedIndex.update(i => i + 1);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.selectedIndex() > 0) {
        this.selectedIndex.update(i => i - 1);
      }
    } else if (event.key === 'Enter' && this.selectedIndex() >= 0) {
      event.preventDefault();
      const item = this.flatResults()[this.selectedIndex()];
      if (item) {
        this.selectItem(item);
      }
    }
  }

  public selectItem(item: SearchResultItem): void {
    this.close();
    switch (item.type) {
      case 'project':
        void this.router.navigate(['/bot-admin', 'project', item.data._id]);
        break;
      case 'bot': {
        void this.router.navigate(['/bot-admin', item.data._id]);
        break;
      }
      case 'document':
        void this.router.navigate(['/bot-admin', 'project', item.data.projectId, 'documents']);
        break;
    }
  }

  public isSelected(index: number): boolean {
    return this.selectedIndex() === index;
  }

  public getItemIndex(type: string, indexInGroup: number): number {
    const results = this.results();
    let offset = 0;
    if (type === 'bot') {
      offset = results?.projects.length ?? 0;
    } else if (type === 'document') {
      offset = (results?.projects.length ?? 0) + (results?.bots.length ?? 0);
    }
    return offset + indexInGroup;
  }

  private async performSearch(query: string): Promise<void> {
    this.loading.set(true);
    try {
      this.results.set(await this.searchService.search(query));
      this.selectedIndex.set(this.flatResults().length > 0 ? 0 : -1);
    } catch {
      this.results.set({ projects: [], bots: [], documents: [] });
      this.selectedIndex.set(-1);
    } finally {
      this.loading.set(false);
    }
  }
}
